import { Injectable } from '@angular/core';
import { Album } from '../models/albums.model';
import { Observable, BehaviorSubject, of, firstValueFrom } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { Mirador } from '../models/mirador';
import { CategoriesService } from './categories.service';
import { MiradorFilter } from '../models/mirador-filter';
import { FILTER_CHIPS, FilterChip } from '../enums & constants/filter-chips.constants';
import { EXPLORE_CUSTOM_LISTS } from '../enums & constants/list.constants';
import { MiradorCustomList } from '../models/mirador-custom-list';
import { Location } from '@angular/common';
import { ListAlbumDetailService } from './list-album-detail.service';

const TABLE = "miradores";
const CHIP_DEFAULT = FILTER_CHIPS[0];

@Injectable({
  providedIn: 'root'
})
export class AlbumsService {

  private allAlbums = new BehaviorSubject<Album[]>([]);
  allAlbums$ = this.allAlbums.asObservable();

  private allAlbumesLoaded = new BehaviorSubject<boolean>(false);
  public $allAlbumesLoaded = this.allAlbumesLoaded.asObservable();

  private allMiradores = new BehaviorSubject<Mirador[]>([]);
  allMiradores$ = this.allMiradores.asObservable();

  private allMiradoresByCity = new BehaviorSubject<Mirador[]>([]);
  allMiradoresByCity$: Observable<Mirador[]> = this.allMiradoresByCity.asObservable();

  private selectedAlbumMiradorsSubject = new BehaviorSubject<Mirador[]>([]);
  selectedAlbumMiradors$: Observable<Mirador[]> = this.selectedAlbumMiradorsSubject.asObservable();

  private miradoresFilteredSubject = new BehaviorSubject<Mirador[]>([]);
  public $miradoresFilteredSubject = this.miradoresFilteredSubject.asObservable();

  private miradoresLoaded = new BehaviorSubject<boolean>(false);
  public $miradoresLoaded = this.miradoresLoaded.asObservable();

  private miradorSelected = new BehaviorSubject<Mirador | null>(null);
  public $miradorSelected = this.miradorSelected.asObservable();

  private currentChipSelected = new BehaviorSubject<FilterChip>(null);
  public $currentChipSelected = this.currentChipSelected.asObservable();

  // Álbumes quemados en el servicio
  private albums: Album[] = [];

  private filters = new BehaviorSubject<MiradorFilter[]>([]);

  constructor(private db: AngularFirestore,
    private categoriesService: CategoriesService,
    private locationService: Location) {
    this.loadAllAlbums();
    this.loadAllMiradores();
    this.filterMiradores();
  }

  // Método para obtener todos los álbumes
  loadAllAlbums() {
    return this.db.collection('albums', ref => ref.orderBy('number_of_viewpoints', 'desc'))
      .valueChanges({ idField: '_id' }).subscribe((data: any[]) => {
        let albumes = data.map((albumData) =>
          new Album(albumData)
        );

        this.allAlbums.next(albumes);
        this.allAlbumesLoaded.next(true);
      })
  }

  // Método para obtener todos los álbumes
  getAllAlbums() {
    return this.allAlbums.value;
  }

  // Método para obtener la información de un álbum por su ID
  getAlbumById(albumId: string): Album {
    return this.allAlbums.value.find(a => a._id === albumId);
  }




  public loadAllMiradores() {
    return this.db.collection(TABLE, ref => ref
      .orderBy('categoryId', 'asc')
      .orderBy("score", 'desc'))
      .valueChanges({ idField: 'miradorId' }).subscribe((data: any[]) => {
        let dataFiltered = data.filter((item) => {

          if (!item.hasOwnProperty('businessHours')) {
            return false;
          }

          if (item.hasOwnProperty('active')) {
            return item.active;
          }
          return true;
        });


        let x = dataFiltered.map((miradorData) =>
          new Mirador(miradorData, this.categoriesService.getCategoryById(miradorData["categoryId"]))
        );

        this.allMiradores.next(x);
        this.miradoresLoaded.next(true);
      })
  }

  public setMiradorSelected(selected: Mirador) {
    this.miradorSelected.next(selected);
  }

  public getMiradorByNormalizedURL(urlMiradorValue: string): Mirador {
    return this.allMiradores.value.find(mirador => {
      return this.getNormalizedMiradorNameForURL(mirador.name) == urlMiradorValue;
    })
  }

  public getMiradoresbyAlbum(albumId): Mirador[] {
    return this.allMiradores.value.filter(mirador => mirador.albums.includes(albumId));
  }

  public getSites(albumId, TypeRequired: number[]): Mirador[] {
    const allSites: Mirador[] = this.allMiradores.value.filter(mirador => mirador.albums.includes(albumId));

    const sitesFilterByType = allSites.filter(mirador => {
      return TypeRequired.includes(Number(mirador.category.id))
    });
    return sitesFilterByType;
  }


  setMiradorsByCity(cityId: string): void {
    const miradoresByCity = this.allMiradores.value.filter(mirador => mirador.albums.includes(cityId));
    this.allMiradoresByCity.next(miradoresByCity);
  }

  setMiradorsByAlbumId(albumId: string): void {

    const miradoresByAlbum = this.allMiradores.value.filter(mirador => mirador.albums.includes(albumId));
    this.selectedAlbumMiradorsSubject.next(miradoresByAlbum);

    // Set to default all
    this.filters.next(CHIP_DEFAULT.filters);
    let currentFilters = this.filters.value;
    this.SetCurrentFilterChipSelected(CHIP_DEFAULT);
    this.miradoresFilteredSubject.next(this.getMiradoresByFilters(currentFilters));
  }

  setMiradorsByAlbumAndCategory(albumId: string, categoryRequited: number[]): void {

    const miradoresByAlbum = this.allMiradores.value.filter(mirador => mirador.albums.includes(albumId) && categoryRequited.includes(Number(mirador.category.id)));
    this.selectedAlbumMiradorsSubject.next(miradoresByAlbum);
    
    // Set to default all
    this.filters.next(CHIP_DEFAULT.filters);
    let currentFilters = this.filters.value;
    this.SetCurrentFilterChipSelected(CHIP_DEFAULT);
    this.miradoresFilteredSubject.next(this.getMiradoresByFilters(currentFilters));
  }



  getMiradorsByAlbum(): Observable<Mirador[]> {
    return this.selectedAlbumMiradors$;
  }

  getMiradorsByCity(): Observable<Mirador[]> {
    return this.allMiradoresByCity$;
  }


  getSitesByType(type): Observable<Mirador[]> {
    return this.selectedAlbumMiradors$;
  }



  filterMiradores(): void {
    this.filters.subscribe(newFilters => {
      let allMiradores = this.selectedAlbumMiradorsSubject.value
      if (allMiradores.length > 0) {
        let miradoresFilt = allMiradores.filter((mirador) => {
          return this.matchAnyFilter(mirador, newFilters);
        });
        this.miradoresFilteredSubject.next(miradoresFilt);
      }
    });
  }

  public getMiradoresAlbumSelected() {
    return this.selectedAlbumMiradorsSubject.value;
  }

  public getMiradoresByFilters(newFilters) {
    const allMiradores = this.selectedAlbumMiradorsSubject.value;

    if (allMiradores.length > 0) {
      const miradoresF = allMiradores.filter((mirador) => {
        return this.matchAnyFilter(mirador, newFilters);
      });
      return miradoresF;
    } else {
      return [];
    }
  }

  public async getMiradoresByIds(miradoresIds: object[]) {
    const allMiradores = await firstValueFrom(this.selectedAlbumMiradors$);
    if (allMiradores.length > 0) {
      return allMiradores.filter((mirador) => {
        return miradoresIds.some((m: any) => m.id === mirador.miradorId);
      });
    }
    return [];
  }

  public getListByListId(listId): MiradorCustomList {
    let lists = EXPLORE_CUSTOM_LISTS;

    return lists.find(list => list.id == listId);
  }

  public async filterByIds(miradoresIds: object[]) {
    let miradoresFound = await this.getMiradoresByIds(miradoresIds);
    if (miradoresFound && miradoresFound.length) {
      this.miradoresFilteredSubject.next(miradoresFound)
    }
  }



  private matchAnyFilter(mirador: Mirador, miradorFilters: MiradorFilter[]): boolean {
    let isMatched = false;

    for (const element of miradorFilters) {
      let evalProp = this.getPropertyValueWithBracketsNotation(mirador, element.type) as string;
      if (evalProp) {
        if (Array.isArray(evalProp)) {
          evalProp = evalProp.join();
        }
        if (this.getNormalizedMiradorNameForURL(evalProp).includes(this.getNormalizedMiradorNameForURL(element.value))) {
          isMatched = true;
          break;
        }
      }
    }
    return isMatched;
  }

  public getNormalizedMiradorNameForURL(value: string) {
    return this.removeAccents(value.trim().toLowerCase().replace(/ /g, "."));
  }

  public removeAccents(value: string) {
    return value.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  }

  public getPropertyValueWithBracketsNotation(object: any, property: string) {
    if (!property.includes('.')) {
      return object[property];
    }

    let splitProperty = property.split('.', 2);

    return this.getPropertyValueWithBracketsNotation(object[splitProperty[0]], splitProperty[1]);
  }

  public setUrlPath(path: string) {
    this.locationService.replaceState("/" + path);
  }

  /*   public getMiradorById(miradorId: string): Mirador | null {
      return this.allMiradores.find(mirador => mirador.miradorId === miradorId) || null;
    } */

  public SetCurrentFilterChipSelected(chip: FilterChip) {
    if (chip == null) {
      this.currentChipSelected.next(null);
    }

    if (chip != this.currentChipSelected.value) {
      if (this.currentChipSelected.value) {
        this.currentChipSelected.value.selected = false;
      }
    } else {
      return;
    }

    chip.selected = !chip.selected;
    this.currentChipSelected.next(chip);
  }

  public resetCurrentChipSelected() {
    if (this.currentChipSelected.value) {
      this.currentChipSelected.value.selected = false;
    }

    this.currentChipSelected.next(null);
  }

  defaultFilterChip(chipDefault: FilterChip) {
    if (this.currentChipSelected.value == null) {
      this.SetCurrentFilterChipSelected(chipDefault);
      this.addFilter(chipDefault.filters);
    }
  }

  public addFilter(filter: MiradorFilter | MiradorFilter[]) {

    if (Array.isArray(filter)) {
      this.filters.next(filter);
    } else if (!Array.isArray(filter)) {
      if (filter.value == 'Todos') {
        filter.value = '';
      }

      //this.listPanelStatus(LIST_PANEL_OPEN); review behaviour
      this.filters.next([filter]);
    }

  }


}
